import { Component, Input, Output, EventEmitter, inject, OnInit, Injectable, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormsModule } from '@angular/forms';
import { MaterialModule } from '../../../shared/material.module';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { DateAdapter, MAT_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';
import { CountryFlat } from '../../../models/country';
import { IndustryFlat } from '../../../models/industry';
import { CompanyDto } from '../../../models/api/companyDto';
import { LutService } from '../../../services/lut.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { IpRange } from '../../../models/ipRange';
import { Helpers } from '../../../utilities/helpers';
import { HotToastService } from '@ngxpert/hot-toast';
import { CompanySelectionDialogComponent } from '../company-selection-dialog/company-selection-dialog.component';
import { CustomerService } from '../../../services/customer.service';
import { ReportVariableCategorySummaryDto } from '../../../models/api/reportVariable';

const DD_MM_YYYY_FORMAT = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Injectable()
class CustomDateAdapter extends NativeDateAdapter {
  override parse(value: any): Date | null {
    if (!value) return null;

    if (typeof value === 'string') {
      const parts = value.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          return new Date(year, month, day);
        }
      }
    }

    return super.parse(value);
  }

  override format(date: Date, displayFormat: Object): string {
    if (!date) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }
}

@Component({
  selector: 'app-customer-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MaterialModule,
    MatChipsModule
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMAT }
  ],
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.scss']
})
export class CustomerFormComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() submitButtonText = 'Submit';
  @Input() isCreateMode = false;
  @Input() set initialSelectedCompanies(value: Array<{ id: string; companyName: string }> | null | undefined) {
    this.selectedCompanies = (value ?? []) as CompanyDto[];
  }
  @Input() set initialIpRanges(value: IpRange[] | null | undefined) {
    this.ipRanges = value ? value.map(r => ({ start: r.start, end: r.end })) : [];
    this.cancelIpRangeEdit();
  }
  @Output() submit = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  private lutService = inject(LutService);
  private customerService = inject(CustomerService);

  countries$: Observable<CountryFlat[]>;
  industries$: Observable<IndustryFlat[]>;

  reportVariableCategories: ReportVariableCategorySummaryDto[] = [];

  readonly measurements: string[] = Helpers.measurements();

  hidePassword = true;
  hideConfirmPassword = true;

  private countries: CountryFlat[] = [];
  private industries: IndustryFlat[] = [];

  // CompanySelectionDialog fetches its own list via CompanyService; this collection only
  // backs chip-name lookup after the dialog returns selected companies.
  selectedCompanies: CompanyDto[] = [];

  // The parent create/edit component reads this on submit and sends the whole list; the API
  // reconciles the stored rows against it and pushes the full set to Keycloak, so an edit is
  // just a replaced entry here — no separate edit call exists or is needed.
  // Always reassigned (never mutated in place) so the mat-table picks the change up.
  ipRanges: IpRange[] = [];
  readonly ipRangeColumns = ['start', 'end', 'actions'];

  ipFrom: string = '';
  ipTo: string = '';

  // Index of the row currently being edited inline, with its draft values.
  editingIpRangeIndex: number | null = null;
  editIpFrom: string = '';
  editIpTo: string = '';
  @ViewChild('editIpFromInput') editIpFromInput?: ElementRef<HTMLInputElement>;

  constructor(private confirmationDialogService: ConfirmationDialogService,
    private toast: HotToastService,
    private dialog: MatDialog) {
    this.countries$ = this.lutService.getVisibleCountries().pipe(
      map(countries => countries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })))
    );
    this.countries$.subscribe(data => {
      this.countries = data;
    });

    this.industries$ = this.lutService.getIndustries().pipe(
      map(industries => industries
        .filter(i => i.parentId === null)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })))
    );
    this.industries$.subscribe(data => {
      this.industries = data;
    });
  }

  ngOnInit() {
    this.customerService.getReportVariableCategories().subscribe(categories => {
      this.reportVariableCategories = categories
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    });
  }

  onSubmit() {
    if (this.form.valid) {
      const confirmation$ = this.isCreateMode
        ? this.confirmationDialogService.openCreateConfirmationDialog()
        : this.confirmationDialogService.openUpdateConfirmationDialog();

      confirmation$.subscribe(result => {
        if (result) {
          this.submit.emit();
        }
      });
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  getCountryName(countryId: string): string {
    return this.countries.find(c => c.id === countryId)?.name || '';
  }

  selectAllCountries() {
    const allCountryIds = this.countries.map(c => c.id);
    this.form.get('countries')?.setValue(allCountryIds);
  }

  deselectAllCountries() {
    this.form.get('countries')?.setValue([]);
  }

  getIndustryName(industryId: string): string {
    return this.industries.find(i => i.id === industryId)?.name || '';
  }

  get sortedSelectedIndustryIds(): string[] {
    const selected = new Set<string>(this.form.get('industries')?.value ?? []);
    return this.industries.filter(i => selected.has(i.id)).map(i => i.id);
  }

  selectAllIndustries() {
    const allIndustryIds = this.industries.map(i => i.id);
    this.form.get('industries')?.setValue(allIndustryIds);
  }

  deselectAllIndustries() {
    this.form.get('industries')?.setValue([]);
  }

  getCompanyName(companyId: string): string {
    return this.selectedCompanies.find(c => c.id === companyId)?.companyName || companyId;
  }

  selectAllCompanies() {
    const allCompanyIds = this.selectedCompanies.map(c => c.id);
    this.form.get('companies')?.setValue(allCompanyIds);
  }

  deselectAllCompanies() {
    this.form.get('companies')?.setValue([]);
  }

  selectAllReportVariables() {
    this.form.get('reportVariables')?.setValue(this.reportVariableCategories.map(c => c.id));
  }

  deselectAllReportVariables() {
    this.form.get('reportVariables')?.setValue([]);
  }

  getReportVariableName(reportVariableId: string): string {
    return this.reportVariableCategories.find(c => c.id === reportVariableId)?.name ?? '';
  }

  getMeasurementName(measurement: string): string {
    return measurement;
  }

  selectAllMeasurements() {
    this.form.get('selectedMeasurements')?.setValue([...this.measurements]);
  }

  deselectAllMeasurements() {
    this.form.get('selectedMeasurements')?.setValue([]);
  }

  getSelectedMeasurementsArray(): string[] {
    const value = this.form.get('selectedMeasurements')?.value;
    return Array.isArray(value) ? value : [];
  }

  compareMeasurements(a: string, b: string): boolean {
    return a === b;
  }

  addIpRange() {
    const range = this.buildIpRange(this.ipFrom, this.ipTo);
    if (!range) {
      return;
    }

    this.ipRanges = [...this.ipRanges, range];
    this.ipFrom = '';
    this.ipTo = '';
  }

  removeIpRange(index: number) {
    this.ipRanges = this.ipRanges.filter((_, i) => i !== index);
    if (this.editingIpRangeIndex === null) {
      return;
    }
    // Keep the edit anchored to the same row now that the indexes have shifted.
    if (this.editingIpRangeIndex === index) {
      this.cancelIpRangeEdit();
    } else if (this.editingIpRangeIndex > index) {
      this.editingIpRangeIndex--;
    }
  }

  isEditingIpRange(index: number): boolean {
    return this.editingIpRangeIndex === index;
  }

  startIpRangeEdit(index: number) {
    const range = this.ipRanges[index];
    if (!range) {
      return;
    }
    this.editingIpRangeIndex = index;
    this.editIpFrom = range.start;
    // A single address is stored as start === end; show it as a single "from" so the row
    // edits the way it was entered.
    this.editIpTo = range.end === range.start ? '' : range.end;

    // The input only exists once the row re-renders in edit mode.
    setTimeout(() => this.editIpFromInput?.nativeElement.focus());
  }

  saveIpRangeEdit() {
    if (this.editingIpRangeIndex === null) {
      return;
    }
    const index = this.editingIpRangeIndex;
    const range = this.buildIpRange(this.editIpFrom, this.editIpTo, index);
    if (!range) {
      return;
    }

    this.ipRanges = this.ipRanges.map((r, i) => i === index ? range : r);
    this.cancelIpRangeEdit();
  }

  cancelIpRangeEdit() {
    this.editingIpRangeIndex = null;
    this.editIpFrom = '';
    this.editIpTo = '';
  }

  /**
   * Validates and normalises a from/to pair into a range, reporting the first problem as a
   * toast. `ignoreIndex` excludes the row being edited from the duplicate check.
   */
  private buildIpRange(from: string, to: string, ignoreIndex: number | null = null): IpRange | null {
    const start = (from ?? '').trim();
    const end = (to ?? '').trim() || start;

    if (!start || !Helpers.isValidIPv4(start) || !Helpers.isValidIPv4(end)) {
      this.toast.error('Invalid IP address');
      return null;
    }
    if (CustomerFormComponent.ipv4ToNumber(start) > CustomerFormComponent.ipv4ToNumber(end)) {
      this.toast.error('The "from" address must not be higher than the "to" address');
      return null;
    }
    const duplicate = this.ipRanges.some((r, i) => i !== ignoreIndex && r.start === start && r.end === end);
    if (duplicate) {
      this.toast.error('This IP range is already in the list');
      return null;
    }

    return { start, end };
  }

  private static ipv4ToNumber(ip: string): number {
    return ip.split('.').reduce((acc, octet) => acc * 256 + parseInt(octet, 10), 0);
  }

  openCompaniesDialog() {
    const dialogRef = this.dialog.open(CompanySelectionDialogComponent, {
      width: '1000px',
      data: {
        selectedCompanies: [...this.selectedCompanies]
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.selectedCompanies = result;
        this.form.get('companies')?.setValue(this.selectedCompanies.map((c: CompanyDto) => c.id));
      }
    });
  }
}
